import React, { useEffect, useState } from 'react';
import { apiFetch } from '../contexts/AuthContext';
import { COLORS } from '../styles/theme';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AlmanacConfig {
  id: string;
  weekStartDay: string;
  defaultBonusWeek: number;
  priorityOrder: string[];
}

interface AlmanacYear {
  id: string;
  year: number;
  totalWeeks: number;
  bonusWeek: number | null;
  firstWeekStart: string;
  seasonRanges: Array<{ season: string; start_week: number; end_week: number }>;
  specialNotes: Array<{ week: number; note: string }>;
}

interface CampClosure {
  id: string;
  campId: string;
  campName: string;
  almanacYearId: string;
  weeks: number[];
  reason: string | null;
}

interface Camp { id: string; name: string; }

const DAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const SEASON_COLORS: Record<string, string> = { spring: '#28A745', summer: '#FFC107', fall: '#FF6B35', winter: '#17A2B8' };

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// Ontario-based week names tied to hunting/fishing/outdoor seasons
// Maps approximate week numbers to activity-based names
const WEEK_NAMES: Record<number, { name: string; tags: string[] }> = {
  1:  { name: 'New Year / Ice Fishing Opens', tags: ['ice-fishing'] },
  2:  { name: 'Ice Fishing', tags: ['ice-fishing'] },
  3:  { name: 'Ice Fishing', tags: ['ice-fishing'] },
  4:  { name: 'Late Ice / Coyote', tags: ['ice-fishing', 'coyote'] },
  5:  { name: 'February Ice', tags: ['ice-fishing'] },
  6:  { name: 'Mid-Winter Ice', tags: ['ice-fishing'] },
  7:  { name: 'Late Winter Ice', tags: ['ice-fishing'] },
  8:  { name: 'Late Winter / Rabbit', tags: ['ice-fishing', 'rabbit'] },
  9:  { name: 'March Break Prep', tags: ['ice-fishing'] },
  10: { name: 'March Break', tags: ['ice-fishing'] },
  11: { name: 'Late Ice / Thaw Watch', tags: ['ice-fishing'] },
  12: { name: 'Ice Out Watch', tags: [] },
  13: { name: 'Spring Thaw / Prep', tags: ['scouting'] },
  14: { name: 'Early Spring / Camp Opening', tags: ['scouting'] },
  15: { name: 'Spring Turkey Opener', tags: ['turkey'] },
  16: { name: 'Spring Turkey', tags: ['turkey'] },
  17: { name: 'Turkey / Walleye Opener', tags: ['turkey', 'walleye'] },
  18: { name: 'Walleye Season', tags: ['walleye', 'turkey'] },
  19: { name: 'Late Spring Fishing', tags: ['walleye', 'bass'] },
  20: { name: 'Victoria Day / Fishing', tags: ['walleye', 'bass'] },
  21: { name: 'Spring Fishing Peak', tags: ['walleye', 'bass', 'trout'] },
  22: { name: 'Bass Pre-Season', tags: ['trout', 'walleye'] },
  23: { name: 'Early Summer / Trout', tags: ['trout', 'walleye'] },
  24: { name: 'Bass Opener Week', tags: ['bass', 'walleye'] },
  25: { name: 'Bass Season', tags: ['bass', 'walleye'] },
  26: { name: 'Canada Day / Bass', tags: ['bass', 'walleye'] },
  27: { name: 'Summer Fishing', tags: ['bass', 'walleye', 'pike'] },
  28: { name: 'Summer Peak', tags: ['bass', 'pike'] },
  29: { name: 'Mid-Summer', tags: ['bass', 'pike'] },
  30: { name: 'Summer / Swimming', tags: ['bass'] },
  31: { name: 'August Fishing', tags: ['bass', 'walleye'] },
  32: { name: 'Civic Holiday', tags: ['bass', 'walleye'] },
  33: { name: 'Late Summer Fishing', tags: ['bass', 'walleye'] },
  34: { name: 'Summer Wind-Down', tags: ['bass'] },
  35: { name: 'Labour Day Prep', tags: ['bass', 'scouting'] },
  36: { name: 'Labour Day / Fall Prep', tags: ['scouting', 'bear'] },
  37: { name: 'Early Bear Season', tags: ['bear', 'grouse'] },
  38: { name: 'Bear / Grouse', tags: ['bear', 'grouse'] },
  39: { name: 'Fall Colours / Grouse', tags: ['grouse', 'duck'] },
  40: { name: 'Thanksgiving / Waterfowl', tags: ['duck', 'goose', 'grouse'] },
  41: { name: 'Waterfowl Season', tags: ['duck', 'goose'] },
  42: { name: 'Deer Bow Opener', tags: ['deer-bow', 'duck'] },
  43: { name: 'Deer Bow / Waterfowl', tags: ['deer-bow', 'duck'] },
  44: { name: 'Moose Opener (Gun)', tags: ['moose', 'deer-bow'] },
  45: { name: 'Moose Week', tags: ['moose'] },
  46: { name: 'Deer Rifle Opener', tags: ['deer-rifle', 'moose'] },
  47: { name: 'Deer Rifle Season', tags: ['deer-rifle'] },
  48: { name: 'Late Deer Rifle', tags: ['deer-rifle'] },
  49: { name: 'Muzzleloader / Late Deer', tags: ['deer-muzzle'] },
  50: { name: 'Winter Prep / Closing', tags: [] },
  51: { name: 'Camp Winterization', tags: [] },
  52: { name: 'Year End / Closed', tags: [] },
  53: { name: 'Bonus / Holiday Week', tags: [] },
};

const TAG_COLORS: Record<string, string> = {
  'ice-fishing': '#17A2B8', 'turkey': '#8B4513', 'walleye': '#007BFF',
  'bass': '#28A745', 'trout': '#20C997', 'pike': '#6F42C1',
  'deer-bow': '#FF6B35', 'deer-rifle': '#DC3545', 'deer-muzzle': '#E83E8C',
  'moose': '#8B0000', 'bear': '#654321', 'grouse': '#B8860B',
  'duck': '#4682B4', 'goose': '#2F4F4F', 'rabbit': '#A0522D',
  'coyote': '#808080', 'scouting': '#888',
};

function getWeekDate(firstWeekStart: string, weekNum: number): string {
  const d = new Date(firstWeekStart);
  d.setDate(d.getDate() + (weekNum - 1) * 7);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function getSeasonForWeek(weekNum: number, seasons: Array<{ season: string; start_week: number; end_week: number }>): string {
  for (const s of seasons) {
    if (weekNum >= s.start_week && weekNum <= s.end_week) return s.season;
  }
  return '';
}

function computeTotalWeeks(year: number, day: string): number {
  const dayMap: Record<string, number> = { sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6 };
  const target = dayMap[day] ?? 6;
  const getFirst = (y: number) => {
    const jan1 = new Date(Date.UTC(y, 0, 1));
    let diff = target - jan1.getUTCDay();
    if (diff < 0) diff += 7;
    return new Date(Date.UTC(y, 0, 1 + diff));
  };
  return Math.round((getFirst(year + 1).getTime() - getFirst(year).getTime()) / (7 * 24 * 60 * 60 * 1000));
}

function getFirstWeekDate(year: number, day: string): string {
  const dayMap: Record<string, number> = { sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6 };
  const target = dayMap[day] ?? 6;
  const jan1 = new Date(Date.UTC(year, 0, 1));
  let diff = target - jan1.getUTCDay();
  if (diff < 0) diff += 7;
  return new Date(Date.UTC(year, 0, 1 + diff)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AlmanacPage() {
  const [config, setConfig] = useState<AlmanacConfig | null>(null);
  const [years, setYears] = useState<AlmanacYear[]>([]);
  const [closures, setClosures] = useState<CampClosure[]>([]);
  const [camps, setCamps] = useState<Camp[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState<AlmanacYear | null>(null);

  // Panel state
  const [panelMode, setPanelMode] = useState<'default' | 'settings' | 'addYear' | 'editBonus'>('default');

  // Settings form
  const [settingsDay, setSettingsDay] = useState('saturday');
  const [settingsBonus, setSettingsBonus] = useState(53);

  // Add year
  const [newYear, setNewYear] = useState(new Date().getFullYear());

  // Edit bonus
  const [editBonusWeek, setEditBonusWeek] = useState(53);

  useEffect(() => { loadAll(); }, []);

  async function loadAll() {
    setLoading(true);
    const [almanacRes, campsRes] = await Promise.all([
      apiFetch<{ config: AlmanacConfig | null; years: AlmanacYear[]; closures: CampClosure[] }>('/api/admin/almanac'),
      apiFetch<Camp[]>('/api/admin/camps'),
    ]);
    if (almanacRes.success && almanacRes.data) {
      setConfig(almanacRes.data.config);
      setYears(almanacRes.data.years || []);
      setClosures(almanacRes.data.closures || []);
      if (almanacRes.data.config) {
        setSettingsDay(almanacRes.data.config.weekStartDay);
        setSettingsBonus(almanacRes.data.config.defaultBonusWeek || 53);
      }
      // Auto-select first year
      if (!selectedYear && almanacRes.data.years?.length) {
        setSelectedYear(almanacRes.data.years[0]);
      }
    }
    if (campsRes.success && campsRes.data) setCamps(campsRes.data);
    setLoading(false);
  }

  async function saveSettings() {
    await apiFetch('/api/admin/almanac', {
      method: 'POST',
      body: JSON.stringify({ week_start_day: settingsDay, default_bonus_week: settingsBonus }),
    });
    setPanelMode('default');
    loadAll();
  }

  async function addYear() {
    await apiFetch('/api/admin/almanac/years', {
      method: 'POST',
      body: JSON.stringify({ year: newYear }),
    });
    setPanelMode('default');
    loadAll();
  }

  async function saveBonusWeek() {
    if (!selectedYear) return;
    await apiFetch(`/api/admin/almanac/years/${selectedYear.year}`, {
      method: 'PATCH',
      body: JSON.stringify({ bonus_week: editBonusWeek }),
    });
    setPanelMode('default');
    loadAll();
  }

  async function deleteYear(year: number) {
    if (!confirm(`Delete almanac year ${year}?`)) return;
    await apiFetch(`/api/admin/almanac/years/${year}`, { method: 'DELETE' });
    if (selectedYear?.year === year) setSelectedYear(null);
    loadAll();
  }

  // --- Camp open/closed toggle ---
  async function toggleCampWeek(campId: string, weekNum: number, yearData: AlmanacYear) {
    const campClosure = closures.find((c) => c.campId === campId && c.almanacYearId === yearData.id);
    const closedWeeks = campClosure ? (campClosure.weeks as number[]) : [];
    const isClosed = closedWeeks.includes(weekNum);

    if (isClosed) {
      // Remove week from closure
      const newWeeks = closedWeeks.filter((w) => w !== weekNum);
      if (newWeeks.length === 0 && campClosure) {
        await apiFetch(`/api/admin/almanac/closures/${campClosure.id}`, { method: 'DELETE' });
      } else if (campClosure) {
        await apiFetch(`/api/admin/almanac/closures/${campClosure.id}`, {
          method: 'PATCH', body: JSON.stringify({ weeks: newWeeks }),
        });
      }
    } else {
      // Add week to closure
      if (campClosure) {
        await apiFetch(`/api/admin/almanac/closures/${campClosure.id}`, {
          method: 'PATCH', body: JSON.stringify({ weeks: [...closedWeeks, weekNum].sort((a, b) => a - b) }),
        });
      } else {
        await apiFetch('/api/admin/almanac/closures', {
          method: 'POST', body: JSON.stringify({ camp_id: campId, almanac_year_id: yearData.id, weeks: [weekNum] }),
        });
      }
    }
    loadAll();
  }

  const currentDay = config?.weekStartDay || settingsDay;

  if (loading) return <div style={st.loading}>Loading almanac...</div>;

  return (
    <div style={st.page}>
      {/* LEFT */}
      <div style={st.leftCol}>
        <div style={st.header}>
          <div>
            <h1 style={st.pageTitle}>Almanac</h1>
            <p style={st.pageSubtitle}>Yearly calendars with per-camp availability</p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={st.btnSecondary} onClick={() => setPanelMode('settings')}>Settings</button>
            <button style={st.btnPrimary} onClick={() => { setNewYear(new Date().getFullYear()); setPanelMode('addYear'); }}>+ Add Year</button>
          </div>
        </div>

        {config && (
          <div style={st.configBar}>
            <span>Start day: <strong>{config.weekStartDay}</strong></span>
            <span>Default bonus week: <strong>{config.defaultBonusWeek || 53}</strong></span>
          </div>
        )}

        {/* Year tabs */}
        {years.length > 0 && (
          <div style={{ display: 'flex', gap: 4, marginBottom: 16 }}>
            {years.map((y) => (
              <button key={y.year} onClick={() => setSelectedYear(y)}
                style={{ ...st.yearTab, ...(selectedYear?.year === y.year ? st.yearTabActive : {}) }}>
                {y.year}
                <span style={{ fontSize: 10, color: COLORS.textMuted, marginLeft: 4 }}>{y.totalWeeks}w</span>
              </button>
            ))}
          </div>
        )}

        {/* Week grid for selected year */}
        {selectedYear && camps.length > 0 && (
          <div style={st.gridCard}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div>
                <h3 style={{ color: COLORS.textPrimary, fontSize: 16, margin: 0 }}>{selectedYear.year} Calendar</h3>
                <span style={{ color: COLORS.textMuted, fontSize: 11 }}>
                  {selectedYear.totalWeeks} weeks · starts {selectedYear.firstWeekStart}
                  {selectedYear.bonusWeek && ` · bonus week ${selectedYear.bonusWeek}`}
                </span>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                {selectedYear.totalWeeks === 53 && (
                  <button style={st.btnSmallMuted} onClick={() => { setEditBonusWeek(selectedYear.bonusWeek || 53); setPanelMode('editBonus'); }}>Edit Bonus</button>
                )}
                <button style={st.btnSmallDanger} onClick={() => deleteYear(selectedYear.year)}>Delete</button>
              </div>
            </div>

            {/* Season legend */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              {(selectedYear.seasonRanges as Array<{ season: string; start_week: number; end_week: number }>).map((s) => (
                <span key={s.season} style={{ ...st.badge, backgroundColor: `${SEASON_COLORS[s.season]}22`, color: SEASON_COLORS[s.season] }}>
                  {s.season} W{s.start_week}–{s.end_week}
                </span>
              ))}
            </div>

            {/* Scrollable week grid */}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', fontSize: 11, width: '100%' }}>
                <thead>
                  <tr>
                    <th style={st.gridTh}>#</th>
                    <th style={st.gridTh}>Date</th>
                    <th style={{ ...st.gridTh, minWidth: 180 }}>Week Name</th>
                    <th style={st.gridTh}>Activities</th>
                    {camps.map((camp) => (
                      <th key={camp.id} style={{ ...st.gridTh, textAlign: 'center', minWidth: 60 }}>{camp.name}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: selectedYear.totalWeeks }, (_, i) => i + 1).map((weekNum) => {
                    const season = getSeasonForWeek(weekNum, selectedYear.seasonRanges as any);
                    const isBonus = selectedYear.bonusWeek === weekNum;
                    const weekInfo = WEEK_NAMES[weekNum] || { name: `Week ${weekNum}`, tags: [] };
                    return (
                      <tr key={weekNum} style={{
                        backgroundColor: isBonus ? 'rgba(255,193,7,0.1)' : undefined,
                        borderLeft: `3px solid ${SEASON_COLORS[season] || '#333'}`,
                      }}>
                        <td style={st.gridTd}>
                          <strong>{weekNum}</strong>
                        </td>
                        <td style={{ ...st.gridTd, color: COLORS.textMuted, whiteSpace: 'nowrap', fontSize: 10 }}>{getWeekDate(selectedYear.firstWeekStart, weekNum)}</td>
                        <td style={st.gridTd}>
                          <div style={{ color: COLORS.textPrimary, fontSize: 12, fontWeight: 500 }}>
                            {weekInfo.name}
                            {isBonus && <span style={{ color: '#FFC107', fontSize: 9, marginLeft: 4 }}>(bonus)</span>}
                          </div>
                        </td>
                        <td style={st.gridTd}>
                          <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                            {weekInfo.tags.map((tag) => (
                              <span key={tag} style={{ display: 'inline-block', padding: '1px 5px', borderRadius: 3, fontSize: 9, fontWeight: 600, backgroundColor: `${TAG_COLORS[tag] || '#888'}22`, color: TAG_COLORS[tag] || '#888' }}>
                                {tag}
                              </span>
                            ))}
                          </div>
                        </td>
                        {camps.map((camp) => {
                          const campClosure = closures.find((c) => c.campId === camp.id && c.almanacYearId === selectedYear.id);
                          const closedWeeks = campClosure ? (campClosure.weeks as number[]) : [];
                          const isOpen = !closedWeeks.includes(weekNum);
                          return (
                            <td key={camp.id} style={{ ...st.gridTd, textAlign: 'center' }}>
                              <input
                                type="checkbox"
                                checked={isOpen}
                                onChange={() => toggleCampWeek(camp.id, weekNum, selectedYear)}
                                style={{ accentColor: COLORS.primaryGreen, cursor: 'pointer' }}
                                title={isOpen ? `${camp.name} open week ${weekNum}` : `${camp.name} closed week ${weekNum}`}
                              />
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {selectedYear && camps.length === 0 && (
          <p style={st.empty}>Add base camps first to manage per-camp availability.</p>
        )}

        {years.length === 0 && (
          <p style={st.empty}>No almanac years configured. Click "+ Add Year" to get started.</p>
        )}
      </div>

      {/* RIGHT: Panel */}
      <div style={st.panelCol}>
        <div style={st.panelContent}>
          {panelMode === 'settings' && (
            <>
              <h3 style={st.panelTitle}>Settings</h3>
              <div style={st.field}>
                <label style={st.label}>Week Start Day</label>
                <select value={settingsDay} onChange={(e) => setSettingsDay(e.target.value)} style={st.input}>
                  {DAYS.map((d) => <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>)}
                </select>
                <span style={{ color: COLORS.textMuted, fontSize: 11 }}>Almanac weeks start on this day</span>
              </div>
              <div style={st.field}>
                <label style={st.label}>Default Bonus Week (53-week years)</label>
                <input type="number" min={1} max={53} value={settingsBonus} onChange={(e) => setSettingsBonus(parseInt(e.target.value, 10) || 53)} style={st.input} />
                <span style={{ color: COLORS.textMuted, fontSize: 11 }}>Which week is the filler in 53-week years</span>
              </div>
              <div style={st.panelActions}>
                <button style={st.btnSecondary} onClick={() => setPanelMode('default')}>Cancel</button>
                <button style={st.btnPrimary} onClick={saveSettings}>Save</button>
              </div>
            </>
          )}

          {panelMode === 'addYear' && (
            <>
              <h3 style={st.panelTitle}>Add Year</h3>
              <div style={st.field}>
                <label style={st.label}>Year</label>
                <input type="number" value={newYear} onChange={(e) => setNewYear(parseInt(e.target.value, 10))} style={st.input} />
              </div>
              <div style={{ padding: 10, backgroundColor: COLORS.backgroundTertiary, borderRadius: 6, marginBottom: 14 }}>
                <div style={{ color: COLORS.textSecondary, fontSize: 12 }}>
                  Week 1 starts: <strong>{getFirstWeekDate(newYear, currentDay)}</strong>
                </div>
                <div style={{ color: COLORS.textSecondary, fontSize: 12 }}>
                  Total weeks: <strong>{computeTotalWeeks(newYear, currentDay)}</strong>
                  {computeTotalWeeks(newYear, currentDay) === 53 && <span style={{ color: '#FFC107' }}> (53-week year)</span>}
                </div>
              </div>
              <div style={st.panelActions}>
                <button style={st.btnSecondary} onClick={() => setPanelMode('default')}>Cancel</button>
                <button style={st.btnPrimary} onClick={addYear}>Create Year</button>
              </div>
            </>
          )}

          {panelMode === 'editBonus' && selectedYear && (
            <>
              <h3 style={st.panelTitle}>Edit Bonus Week – {selectedYear.year}</h3>
              <div style={st.field}>
                <label style={st.label}>Bonus Filler Week</label>
                <input type="number" min={1} max={53} value={editBonusWeek} onChange={(e) => setEditBonusWeek(parseInt(e.target.value, 10) || 53)} style={st.input} />
              </div>
              <div style={st.panelActions}>
                <button style={st.btnSecondary} onClick={() => setPanelMode('default')}>Cancel</button>
                <button style={st.btnPrimary} onClick={saveBonusWeek}>Update</button>
              </div>
            </>
          )}

          {panelMode === 'default' && (
            <>
              <h3 style={st.panelTitle}>Almanac</h3>
              <p style={{ color: COLORS.textMuted, fontSize: 13, marginBottom: 16 }}>
                {years.length} year{years.length !== 1 ? 's' : ''} configured.
                Use the checkboxes in the calendar to mark which base camps are open each week.
              </p>
              <p style={{ color: COLORS.textMuted, fontSize: 12 }}>
                <strong style={{ color: COLORS.textSecondary }}>Checked</strong> = camp is open that week<br />
                <strong style={{ color: COLORS.textSecondary }}>Unchecked</strong> = camp is closed
              </p>
              <div style={{ marginTop: 16 }}>
                <button style={{ ...st.btnSecondary, width: '100%', marginBottom: 8 }} onClick={() => setPanelMode('settings')}>
                  Edit Settings
                </button>
                <button style={{ ...st.btnPrimary, width: '100%' }} onClick={() => { setNewYear(new Date().getFullYear()); setPanelMode('addYear'); }}>
                  + Add Year
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const st: Record<string, React.CSSProperties> = {
  page: { display: 'flex', height: 'calc(100vh - 60px)', gap: 0 },
  leftCol: { flex: 1, overflowY: 'auto', padding: '0 20px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, paddingTop: 12 },
  pageTitle: { fontSize: 22, fontWeight: 700, color: COLORS.textPrimary, margin: 0 },
  pageSubtitle: { fontSize: 13, color: COLORS.textMuted, marginTop: 4 },
  configBar: { display: 'flex', gap: 20, padding: '10px 16px', backgroundColor: COLORS.backgroundSecondary, borderRadius: 8, marginBottom: 16, fontSize: 12, color: COLORS.textSecondary },

  yearTab: { padding: '8px 16px', backgroundColor: 'transparent', color: COLORS.textMuted, border: `1px solid ${COLORS.borderDefault}`, borderRadius: '8px 8px 0 0', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  yearTabActive: { backgroundColor: COLORS.backgroundSecondary, color: COLORS.textGold, borderBottomColor: COLORS.backgroundSecondary },

  gridCard: { backgroundColor: COLORS.backgroundSecondary, border: `1px solid ${COLORS.borderDefault}`, borderRadius: 12, padding: 16, marginBottom: 16 },
  gridTh: { padding: '6px 8px', textAlign: 'left' as const, color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.borderDefault}`, fontSize: 11, fontWeight: 600, textTransform: 'uppercase' as const, position: 'sticky' as const, top: 0, backgroundColor: COLORS.backgroundSecondary },
  gridTd: { padding: '4px 8px', borderBottom: `1px solid ${COLORS.borderDefault}`, color: COLORS.textPrimary },

  panelCol: { width: 340, flexShrink: 0, overflowY: 'auto', borderLeft: `1px solid ${COLORS.borderDefault}`, backgroundColor: COLORS.backgroundSecondary },
  panelContent: { padding: 20 },
  panelTitle: { fontSize: 18, fontWeight: 600, color: COLORS.textPrimary, margin: '0 0 16px' },
  panelActions: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 },

  field: { display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 14 },
  label: { fontSize: 11, fontWeight: 600, color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 },
  input: { padding: '7px 10px', backgroundColor: COLORS.backgroundTertiary, border: `1px solid ${COLORS.borderDefault}`, borderRadius: 6, color: COLORS.textPrimary, fontSize: 13 },
  badge: { display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 },
  empty: { color: COLORS.textMuted, fontSize: 13, fontStyle: 'italic' },
  loading: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: COLORS.textMuted },

  btnPrimary: { padding: '7px 14px', backgroundColor: COLORS.primaryGreen, color: COLORS.textGold, border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  btnSecondary: { padding: '7px 14px', backgroundColor: COLORS.backgroundTertiary, color: COLORS.textSecondary, border: `1px solid ${COLORS.borderDefault}`, borderRadius: 6, fontSize: 12, cursor: 'pointer' },
  btnSmallMuted: { padding: '4px 10px', backgroundColor: 'transparent', color: COLORS.textMuted, border: `1px solid ${COLORS.borderDefault}`, borderRadius: 4, fontSize: 11, cursor: 'pointer' },
  btnSmallDanger: { padding: '4px 10px', backgroundColor: 'transparent', color: COLORS.statusError, border: `1px solid rgba(220,53,69,0.3)`, borderRadius: 4, fontSize: 11, cursor: 'pointer' },
};
